<template>
  <HeaderWithIcon :domain="domain" variant="stacked" :hide-icon="member.kind === 'contact' && !showContactAccountIcon">
    <template #primary>
      <template v-if="member.kind === 'contact'">
        <span class="primaryName">{{ contactName || contactEmail || '—' }}</span>
        <span v-if="accountName" class="atCompany"> @ {{ accountName }}</span>
      </template>
      <template v-else>
        <span class="primaryName">{{ accountName || domain || '—' }}</span>
      </template>
    </template>
    <template #secondary>
      <template v-if="member.kind === 'contact'">
        <span class="domainWithBadges">
          <span v-if="contactEmail && contactName">{{ contactEmail }}</span>
          <span v-if="contactTitle" class="contactTitle" :class="{ contactTitleStandalone: !(contactEmail && contactName) }">
            {{ contactEmail && contactName ? `(${contactTitle})` : contactTitle }}
          </span>
        </span>
      </template>
      <template v-else>
        <span v-if="domain" class="domainWithBadges">
          <span>{{ domain }}</span>
        </span>
      </template>
    </template>
  </HeaderWithIcon>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import HeaderWithIcon from '../../components/HeaderWithIcon.vue'

type Member = {
  kind: 'account' | 'contact'
  account_domain?: string | null
  account_name?: string | null
  contact_name?: string | null
  contact_email?: string | null
  contact_title?: string | null
}

const props = withDefaults(defineProps<{
  member: Member
  /** Show the account favicon for a contact row too — the row itself omits
   *  it (the account is already shown separately in that column), but a
   *  standalone context like a modal header has nowhere else for it. */
  showContactAccountIcon?: boolean
}>(), {
  showContactAccountIcon: false,
})

const domain = computed(() => props.member.account_domain || '')
const accountName = computed(() => props.member.account_name || '')
const contactName = computed(() => props.member.contact_name || '')
const contactTitle = computed(() => props.member.contact_title || '')
const contactEmail = computed(() => props.member.contact_email || '')
</script>

<style scoped>
.primaryName {
  font-weight: 700;
  color: var(--color-text-primary, var(--text));
}

.atCompany {
  font-weight: 500;
  color: var(--color-text-secondary, var(--muted));
}

.contactTitle {
  margin-left: 6px;
  color: var(--color-text-tertiary, var(--muted));
}
.contactTitleStandalone {
  margin-left: 0;
}

.domainWithBadges {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
</style>
